/**
 * #1106 — sanitize and normalize TextArea HTML for persistence.
 */
import sanitizeHtml from 'sanitize-html';

import { convertToPlainText } from './convertToPlainText';
import {
  MULTILINE_RICH_HTML_FIELD_LIMITS,
  multilineRichHtmlMaxLength,
} from './multilineRichHtmlFields';
import { rewriteHtmlPreviewSrcsToPendingRefs } from '../../services/pendingInlineImagePreview';

export { convertToPlainText };

const PENDING_ATTACHMENT_PATTERN = /sitescope-attachment-pending:\/\//i;

const DISALLOWED_PERSIST_PATTERNS: ReadonlyArray<RegExp> = [
  /<script[\s>]/i,
  /javascript:/i,
  /data:text\/html/i,
  /data:image\/[a-z0-9+.-]+;base64,/i,
  PENDING_ATTACHMENT_PATTERN,
  // Attribute-position only. `/on\w+\s*=/i` also matches ordinary prose
  // ("Monitoring=yes", "Contact=John") and blocks save.
  /<[^>]*\son\w+\s*=/i,
];

const ALLOWED_TAGS = [
  'p',
  'br',
  'div',
  'span',
  'strong',
  'b',
  'em',
  'i',
  'u',
  's',
  'strike',
  'del',
  'h1',
  'h2',
  'h3',
  'h4',
  'ul',
  'ol',
  'li',
  'a',
  'blockquote',
  'hr',
  'sub',
  'sup',
  'table',
  'thead',
  'tbody',
  'tfoot',
  'tr',
  'td',
  'th',
  'code',
  'pre',
  'img',
  'mark',
];
const SAFE_STYLE_VALUE = /^(?!.*(?:expression|url\s*\()).+$/i;
const ALLOWED_STYLES: Readonly<Record<string, RegExp[]>> = {
  'font-size': [SAFE_STYLE_VALUE],
  'font-weight': [SAFE_STYLE_VALUE],
  'font-style': [SAFE_STYLE_VALUE],
  'font-family': [SAFE_STYLE_VALUE],
  'text-decoration': [SAFE_STYLE_VALUE],
  'text-align': [SAFE_STYLE_VALUE],
  color: [SAFE_STYLE_VALUE],
  'background-color': [SAFE_STYLE_VALUE],
};
const PERSISTED_ATTACHMENT_SRC = /^sitescope-attachment:\/\/\d+\/?$/;
const PENDING_ATTACHMENT_SRC = /^sitescope-attachment-pending:\/\/[\w-]+$/;
const REMOTE_HTTP_IMAGE_SRC = /^https?:\/\//i;

const P_CLOSE = '</p>';

type BlankParagraph = {
  open: number;
  closeEnd: number;
  attrs: string;
};

function isParagraphOpenChar(ch: string): boolean {
  return ch === '>' || ch === ' ' || ch === '\t' || ch === '\n' || ch === '\r';
}

function nextParagraphOpen(html: string, from: number): number {
  let pos = from;
  while (pos < html.length) {
    const open = html.indexOf('<p', pos);
    if (open < 0) {
      return -1;
    }
    if (isParagraphOpenChar(html.charAt(open + 2))) {
      return open;
    }
    pos = open + 2;
  }
  return -1;
}

/** Empty inner HTML, or a single `<br>` / `<br …>` tag (no nested markup). */
function isBlankParagraphInner(inner: string): boolean {
  if (inner === '') {
    return true;
  }
  if (!inner.startsWith('<br') || !inner.endsWith('>')) {
    return false;
  }
  return inner.indexOf('<', 1) === -1;
}

function collectBlankParagraphs(html: string): BlankParagraph[] {
  const blanks: BlankParagraph[] = [];
  let pos = 0;
  while (pos < html.length) {
    const open = nextParagraphOpen(html, pos);
    if (open < 0) {
      break;
    }
    const gt = html.indexOf('>', open + 2);
    if (gt < 0) {
      break;
    }
    const close = html.indexOf(P_CLOSE, gt + 1);
    if (close < 0) {
      break;
    }
    const inner = html.slice(gt + 1, close);
    if (!inner.includes('<p') && isBlankParagraphInner(inner)) {
      blanks.push({
        open,
        closeEnd: close + P_CLOSE.length,
        attrs: html.slice(open + 2, gt),
      });
    }
    pos = close + P_CLOSE.length;
  }
  return blanks;
}

function spliceBlankParagraphs(
  html: string,
  blanks: BlankParagraph[],
  fill: (attrs: string) => string,
): string {
  let out = '';
  let pos = 0;
  for (const blank of blanks) {
    out += html.slice(pos, blank.open);
    out += fill(blank.attrs);
    pos = blank.closeEnd;
  }
  return out + html.slice(pos);
}

/**
 * One canonical blank line. `<p></p>` can lose its height once a saved request
 * is reopened, and `<p><br /></p>` renders as two lines under
 * react-native-render-html, so a lone non-breaking space is the only form that
 * is exactly one blank line on both web and native. Allowed `style` on `<p>`
 * (e.g. text-align) is kept so an aligned blank does not collapse or lose
 * alignment. A document made only of blank paragraphs is left alone so an
 * empty field still stores as empty.
 */
function normalizeBlankParagraphs(html: string): string {
  const blanks = collectBlankParagraphs(html);
  if (blanks.length === 0) {
    return html;
  }
  if (!spliceBlankParagraphs(html, blanks, () => '').trim()) {
    return html;
  }
  return spliceBlankParagraphs(html, blanks, attrs => `<p${attrs}>\u00a0</p>`);
}

function isAllowedPersistImageSrc(
  src: string,
  allowPendingAttachmentRefs: boolean,
): boolean {
  const trimmed = src.trim();
  if (PERSISTED_ATTACHMENT_SRC.test(trimmed)) {
    return true;
  }
  return allowPendingAttachmentRefs && PENDING_ATTACHMENT_SRC.test(trimmed);
}

function isAllowedDisplayImageSrc(
  src: string,
  allowPendingAttachmentRefs: boolean,
  allowRemoteHttpImages: boolean,
  extraImgSchemes: readonly string[],
): boolean {
  if (isAllowedPersistImageSrc(src, allowPendingAttachmentRefs)) {
    return true;
  }
  if (allowRemoteHttpImages && REMOTE_HTTP_IMAGE_SRC.test(src.trim())) {
    return true;
  }
  return isExtraSchemeImageSrc(src, extraImgSchemes);
}

/**
 * `<scheme>://<digits>`, exactly, for a caller-supplied scheme (#1530). The
 * shape is deliberately as narrow as the server's own message-image filter
 * (`correspondenceSanitizer.ts`, `MESSAGE_IMAGE_SRC`): no path, no query,
 * digits only, so a stored message ref and nothing broader survives.
 */
function isExtraSchemeImageSrc(
  src: string,
  extraImgSchemes: readonly string[],
): boolean {
  if (extraImgSchemes.length === 0) {
    return false;
  }
  const trimmed = src.trim();
  const separator = trimmed.indexOf('://');
  if (separator <= 0) {
    return false;
  }
  return (
    extraImgSchemes.includes(trimmed.slice(0, separator)) &&
    /^\d+$/.test(trimmed.slice(separator + 3))
  );
}

/** True when the stored value is legacy plain text (no HTML tags). */
export function isLegacyPlainTextAreaValue(value: string): boolean {
  const trimmed = value.trim();
  if (!trimmed) {
    return true;
  }
  return !/<[a-z][\s\S]*>/i.test(trimmed);
}

export function containsDisallowedPersistContent(
  value: string,
  allowPendingAttachmentRefs = false,
): boolean {
  return DISALLOWED_PERSIST_PATTERNS.some(pattern => {
    if (allowPendingAttachmentRefs && pattern === PENDING_ATTACHMENT_PATTERN) {
      return false;
    }
    return pattern.test(value);
  });
}

/** Allow-list sanitize for stored TextArea HTML (client before API). */
export function sanitizeTextArea(
  html: string,
  options?: {
    allowPendingAttachmentRefs?: boolean;
    /** View-only: keep http(s) images the editor already shows. Do not persist. */
    allowRemoteHttpImages?: boolean;
    /**
     * Extra `img` src schemes to keep, each accepted ONLY in the exact
     * `<scheme>://<digits>` form (#1530; correspondence passes
     * `['correspondence-file']` so a message body reuses this ONE sanitizer
     * rather than a copy that stops receiving fixes). Default: none, so every
     * existing caller is unchanged.
     */
    imgSchemes?: readonly string[];
    /**
     * House tags to SUBTRACT from the allow-list for this call, text kept
     * (#1530 Teams parity). Correspondence passes the server's
     * `MESSAGE_DROPPED_TAGS` mirror so the composer never previews or sends
     * what the message rule drops. Default: none, every request field
     * unchanged.
     */
    omitTags?: readonly string[];
    /** House style properties to subtract, likewise (`text-align`, ...). */
    omitStyles?: readonly string[];
    /**
     * Extra attributes to ADMIT per tag for this call, merged onto the house
     * list (#1599; correspondence passes `{ span: MENTION_SPAN_ATTRIBUTES }`
     * so a mention token survives the composer's preview and send). Default:
     * none, every request field unchanged.
     */
    extraAttributes?: Readonly<Record<string, readonly string[]>>;
  },
): string {
  if (!html.trim()) {
    return '';
  }

  const allowPendingAttachmentRefs =
    options?.allowPendingAttachmentRefs ?? false;
  const allowRemoteHttpImages = options?.allowRemoteHttpImages ?? false;
  const extraImgSchemes = options?.imgSchemes ?? [];
  const omitTags = options?.omitTags ?? [];
  const omitStyles = options?.omitStyles ?? [];
  const allowedAttributes: Record<string, string[]> = {
    '*': ['style'],
    a: ['href', 'title', 'rel', 'target'],
    img: ['src', 'alt', 'title'],
    table: ['data-ss-grid'],
    td: ['colspan', 'rowspan'],
    th: ['colspan', 'rowspan'],
    mark: ['data-color'],
  };
  // A Map lookup over the caller's tag names, never `allowedAttributes[tag]`
  // on a plain object: a tag named `constructor` would resolve on the
  // prototype (correspondence.md, "Stored is served").
  const houseAttributes = new Map(Object.entries(allowedAttributes));
  for (const [tag, extra] of Object.entries(options?.extraAttributes ?? {})) {
    houseAttributes.set(tag, [...(houseAttributes.get(tag) ?? []), ...extra]);
  }
  const allowedTags =
    omitTags.length === 0
      ? ALLOWED_TAGS
      : ALLOWED_TAGS.filter(tag => !omitTags.includes(tag));
  const allowedStyles =
    omitStyles.length === 0
      ? ALLOWED_STYLES
      : Object.fromEntries(
          Object.entries(ALLOWED_STYLES).filter(
            ([property]) => !omitStyles.includes(property),
          ),
        );
  const imgSchemes = [
    'sitescope-attachment',
    ...(allowPendingAttachmentRefs ? ['sitescope-attachment-pending'] : []),
    ...(allowRemoteHttpImages ? ['http', 'https'] : []),
    ...extraImgSchemes,
  ];

  const sanitized = sanitizeHtml(html, {
    allowedTags,
    allowedAttributes: Object.fromEntries(houseAttributes),
    allowedSchemes: ['http', 'https', 'mailto', 'tel'],
    allowedSchemesByTag: {
      img: imgSchemes,
    },
    allowedStyles: { '*': allowedStyles },
    exclusiveFilter: frame =>
      frame.tag === 'img' &&
      !isAllowedDisplayImageSrc(
        frame.attribs.src?.trim() ?? '',
        allowPendingAttachmentRefs,
        allowRemoteHttpImages,
        extraImgSchemes,
      ),
    transformTags: {
      table: (tagName, attribs) => {
        const next = { ...attribs };
        if (next['data-ss-grid'] !== '1') {
          delete next['data-ss-grid'];
        }
        return { tagName, attribs: next };
      },
      a: (_tagName, attribs) => ({
        tagName: 'a',
        attribs: {
          ...attribs,
          rel: 'noopener noreferrer',
          target: '_blank',
        },
      }),
    },
  }).trim();

  return normalizeBlankParagraphs(sanitized);
}

export type TextAreaValidationResult =
  | { ok: true; normalized: string }
  | { ok: false; message: string };

export function validateTextAreaField(
  value: string,
  options?: {
    maxPlainTextLength?: number;
    fieldLabel?: string;
    allowPendingAttachmentRefs?: boolean;
    /** Drop pending refs instead of rejecting (create payload before first id). */
    stripPendingAttachmentRefs?: boolean;
  },
): TextAreaValidationResult {
  const trimmed = rewriteHtmlPreviewSrcsToPendingRefs(value).trim();
  if (!trimmed) {
    return { ok: true, normalized: '' };
  }

  const allowPendingAttachmentRefs =
    options?.allowPendingAttachmentRefs ?? false;
  const stripPendingAttachmentRefs =
    options?.stripPendingAttachmentRefs ?? false;

  if (
    containsDisallowedPersistContent(
      trimmed,
      allowPendingAttachmentRefs || stripPendingAttachmentRefs,
    )
  ) {
    return {
      ok: false,
      message: 'TextArea content contains disallowed markup or embedded images',
    };
  }

  const normalized = isLegacyPlainTextAreaValue(trimmed)
    ? trimmed
    : sanitizeTextArea(trimmed, {
        allowPendingAttachmentRefs,
        // Vendor/GitLab mailer logos are https. Rehosting them from the
        // browser is CORS-blocked; stripping on save made paste-then-reload
        // drop the same images the editor just showed (#1527). Persist https
        // display URLs only — never blob:, cid:, Base64, or javascript:.
        allowRemoteHttpImages: true,
      });

  if (
    containsDisallowedPersistContent(normalized, allowPendingAttachmentRefs)
  ) {
    return {
      ok: false,
      message: 'TextArea content contains disallowed markup or embedded images',
    };
  }

  if (options?.maxPlainTextLength != null) {
    const plainLength = convertToPlainText(normalized).length;
    if (plainLength > options.maxPlainTextLength) {
      const label = options.fieldLabel ?? 'Field';
      return {
        ok: false,
        message: `${label} must be ${options.maxPlainTextLength.toLocaleString()} characters or less`,
      };
    }
  }

  return { ok: true, normalized };
}

/** Normalize a TextArea value immediately before save / Zod input build. */
export function normalizeTextAreaForSave(value: string): string {
  const result = validateTextAreaField(value);
  return result.ok ? result.normalized : value.trim();
}

export const TEXT_AREA_TRANSPORT_FIELD_KEYS = [
  ...Object.keys(MULTILINE_RICH_HTML_FIELD_LIMITS),
  'fb_comments',
] as const;

/** Prepare TextArea fields before API or offline-queue transport. */
export function prepareTextAreaFieldsForTransport<
  T extends Record<string, unknown>,
>(
  input: T,
  options?: {
    allowPendingAttachmentRefs?: boolean;
    stripPendingAttachmentRefs?: boolean;
  },
): T {
  const prepared: Record<string, unknown> = { ...input };

  for (const key of TEXT_AREA_TRANSPORT_FIELD_KEYS) {
    const value = prepared[key];
    if (value === undefined || value === null) {
      continue;
    }
    if (typeof value !== 'string') {
      throw new Error(`${key} must be a string`);
    }
    const result = validateTextAreaField(value, {
      maxPlainTextLength: multilineRichHtmlMaxLength(key),
      fieldLabel: key,
      allowPendingAttachmentRefs: options?.allowPendingAttachmentRefs,
      stripPendingAttachmentRefs: options?.stripPendingAttachmentRefs,
    });
    if (!result.ok) {
      throw new Error(result.message);
    }
    prepared[key] = result.normalized;
  }

  return prepared as T;
}
